import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, card, btnPrimary, inp } from "./AdminShell";
import { Plus, Edit, Copy, Trash2, Search, Star, StarOff, ExternalLink, ArrowUpDown } from "lucide-react";

const STOCK_OPTS = ["Tersedia", "Terjual", "Segera Hadir", "Hubungi Kami"];
const COND_OPTS = ["Baru", "Rekondisi", "Bekas", "Hubungi Kami"];
const stockColor = (s) => ({ "Tersedia": "bg-green-50 text-green-700", "Terjual": "bg-neutral-100 text-neutral-500", "Segera Hadir": "bg-blue-50 text-blue-700", "Hubungi Kami": "bg-[#FF9418]/10 text-[#FF9418]" }[s] || "bg-neutral-100");

export function AdminProducts() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fBrand, setFBrand] = useState("");
  const [fStock, setFStock] = useState("");
  const [fCondition, setFCondition] = useState("");
  const [fPublished, setFPublished] = useState("");
  const [sortKey, setSortKey] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const PER = 20;

  const refresh = () => { setLoading(true); api.get("/admin/products").then((r) => setList(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { refresh(); }, []);

  const brands = useMemo(() => [...new Set(list.map((p) => p.brand))].filter(Boolean).sort(), [list]);
  const cats = useMemo(() => [...new Set(list.map((p) => p.category))].filter(Boolean).sort(), [list]);

  const filtered = useMemo(() => {
    let out = list;
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((p) => (p.product_name || "").toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q) || (p.model || "").toLowerCase().includes(q));
    }
    if (fCategory) out = out.filter((p) => p.category === fCategory);
    if (fBrand) out = out.filter((p) => p.brand === fBrand);
    if (fStock) out = out.filter((p) => p.stock_status === fStock);
    if (fCondition) out = out.filter((p) => (p.condition || "Hubungi Kami") === fCondition);
    if (fPublished === "published") out = out.filter((p) => p.published);
    if (fPublished === "draft") out = out.filter((p) => !p.published);
    out = [...out].sort((a, b) => {
      const av = a[sortKey] || ""; const bv = b[sortKey] || "";
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return out;
  }, [list, search, fCategory, fBrand, fStock, fCondition, fPublished, sortKey, sortDir]);

  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / PER));
  const paged = filtered.slice((page - 1) * PER, page * PER);
  useEffect(() => { setPage(1); }, [search, fCategory, fBrand, fStock, fCondition, fPublished]);

  const quickUpdate = async (id, patch) => {
    try {
      await api.patch(`/admin/products/${id}`, patch);
      toast.success("Produk diperbarui.");
      setList((prev) => prev.map((p) => p.id === id ? { ...p, ...patch } : p));
    } catch { toast.error("Gagal memperbarui"); }
  };

  const dup = async (id) => {
    try { await api.post(`/admin/products/${id}/duplicate`); toast.success("Produk digandakan."); refresh(); }
    catch { toast.error("Gagal menggandakan"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Hapus produk "${name}"?`)) return;
    try { await api.delete(`/admin/products/${id}`); toast.success("Produk dihapus."); refresh(); }
    catch { toast.error("Gagal menghapus"); }
  };

  const togglePublish = async (p) => {
    if (p.published && !window.confirm(`Sembunyikan "${p.product_name}" dari website?`)) return;
    quickUpdate(p.id, { published: !p.published });
  };

  const sortBy = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  const Th = ({ k, children, className = "" }) => (
    <th className={`text-left px-3 py-3 cursor-pointer select-none ${className}`} onClick={() => sortBy(k)}>
      <span className="flex items-center gap-1">{children}{sortKey === k && <ArrowUpDown size={11} />}</span>
    </th>
  );

  return (
    <div>
      <PageHeader title="Products" subtitle={`${list.length} produk di katalog`}
        actions={<Link to="/admin/products/new"><Button className={`${btnPrimary} h-10 px-4`} data-testid="new-product-btn"><Plus size={14} /> Produk Baru</Button></Link>} />

      {/* Filters */}
      <div className={`${card} p-4 mb-4`}>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama, brand, atau model..." className="w-full bg-white border border-neutral-300 rounded-md pl-9 pr-3 py-2 text-sm outline-none focus:border-[#FF9418]" data-testid="prod-search" />
          </div>
          <select value={fBrand} onChange={(e) => setFBrand(e.target.value)} className="bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF9418]" data-testid="prod-f-brand">
            <option value="">Semua Brand</option>{brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} className="bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF9418]" data-testid="prod-f-cat">
            <option value="">Semua Kategori</option>{cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={fStock} onChange={(e) => setFStock(e.target.value)} className="bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF9418]" data-testid="prod-f-stock">
            <option value="">Semua Ketersediaan</option>{STOCK_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fCondition} onChange={(e) => setFCondition(e.target.value)} className="bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF9418]">
            <option value="">Semua Kondisi</option>{COND_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fPublished} onChange={(e) => setFPublished(e.target.value)} className="bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF9418]">
            <option value="">Semua Publish</option><option value="published">Published</option><option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Desktop table */}
      <div className={`${card} overflow-hidden hidden md:block`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-3 w-14">Foto</th>
                <Th k="product_name">Produk</Th>
                <Th k="brand">Brand</Th>
                <Th k="category">Kategori</Th>
                <th className="text-left px-3 py-3">Ketersediaan</th>
                <th className="text-left px-3 py-3">Featured</th>
                <th className="text-left px-3 py-3">Publish</th>
                <Th k="updated_at">Diperbarui</Th>
                <th className="text-right px-3 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading ? <tr><td colSpan="9" className="text-center py-10 text-neutral-500">Memuat...</td></tr> :
                paged.length === 0 ? <tr><td colSpan="9" className="text-center py-10 text-neutral-500">Tidak ada produk cocok filter.</td></tr> :
                  paged.map((p) => (
                    <tr key={p.id} className="hover:bg-neutral-50" data-testid={`prod-row-${p.slug}`}>
                      <td className="px-3 py-2">
                        <div className="w-10 h-10 bg-neutral-100 rounded overflow-hidden flex items-center justify-center text-[9px] text-neutral-400 font-mono">
                          {p.featured_image ? <img src={p.featured_image} className="w-full h-full object-cover" alt="" /> : "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/admin/products/${p.id}`} className="font-semibold text-neutral-900 hover:text-[#FF9418] block">{p.product_name}</Link>
                        <div className="text-[11px] text-neutral-500">{p.model}</div>
                      </td>
                      <td className="px-3 py-2 text-neutral-700">{p.brand}</td>
                      <td className="px-3 py-2 text-neutral-700 text-xs">{p.category}</td>
                      <td className="px-3 py-2">
                        <select value={p.stock_status} onChange={(e) => quickUpdate(p.id, { stock_status: e.target.value })}
                          className={`text-[11px] font-mono uppercase tracking-wider rounded px-2 py-1 border-none outline-none cursor-pointer ${stockColor(p.stock_status)}`}
                          data-testid={`stock-${p.slug}`}>
                          {STOCK_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => quickUpdate(p.id, { featured: !p.featured })} data-testid={`feat-${p.slug}`}>
                          {p.featured ? <Star size={16} className="fill-[#FF9418] text-[#FF9418]" /> : <StarOff size={16} className="text-neutral-300 hover:text-[#FF9418]" />}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => togglePublish(p)} data-testid={`pub-${p.slug}`}
                          className={`w-9 h-5 rounded-full relative transition ${p.published ? "bg-[#FF9418]" : "bg-neutral-300"}`}>
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition ${p.published ? "left-4" : "left-0.5"}`} />
                        </button>
                      </td>
                      <td className="px-3 py-2 text-[11px] text-neutral-500 whitespace-nowrap font-mono">
                        {p.updated_at ? new Date(p.updated_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <a href={`/produk/${p.slug}${!p.published ? "?preview=1" : ""}`} target="_blank" rel="noreferrer" className="text-neutral-500 hover:text-[#FF9418] inline-block p-1.5" title="Preview"><ExternalLink size={14} /></a>
                        <Link to={`/admin/products/${p.id}`} className="text-neutral-500 hover:text-[#FF9418] inline-block p-1.5" data-testid={`edit-${p.slug}`}><Edit size={14} /></Link>
                        <button onClick={() => dup(p.id)} className="text-neutral-500 hover:text-[#FF9418] p-1.5" title="Duplicate"><Copy size={14} /></button>
                        <button onClick={() => del(p.id, p.product_name)} className="text-neutral-500 hover:text-red-600 p-1.5" data-testid={`del-${p.slug}`}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className={`${card} md:hidden divide-y divide-neutral-100 overflow-hidden`}>
        {loading ? <div className="p-6 text-center text-neutral-500">Memuat...</div> :
          paged.map((p) => (
            <div key={p.id} className="p-3 flex gap-3">
              <div className="w-14 h-14 bg-neutral-100 rounded overflow-hidden flex-shrink-0">
                {p.featured_image ? <img src={p.featured_image} className="w-full h-full object-cover" alt="" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/admin/products/${p.id}`} className="font-semibold text-neutral-900 block truncate">{p.product_name}</Link>
                <div className="text-xs text-neutral-500 mb-2">{p.brand} · {p.category}</div>
                <div className="flex flex-wrap gap-1.5 items-center">
                  <select value={p.stock_status} onChange={(e) => quickUpdate(p.id, { stock_status: e.target.value })}
                    className={`text-[10px] font-mono uppercase rounded px-2 py-1 border-none outline-none ${stockColor(p.stock_status)}`}>
                    {STOCK_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => quickUpdate(p.id, { featured: !p.featured })}>
                    {p.featured ? <Star size={14} className="fill-[#FF9418] text-[#FF9418]" /> : <StarOff size={14} className="text-neutral-300" />}
                  </button>
                  <div className="ml-auto flex gap-1">
                    <Link to={`/admin/products/${p.id}`} className="text-neutral-500 p-1"><Edit size={13} /></Link>
                    <button onClick={() => del(p.id, p.product_name)} className="text-neutral-500 p-1"><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <div className="text-neutral-500">Halaman {page} dari {pages} · {total} produk</div>
          <div className="flex gap-1">
            <Button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} variant="outline" className="h-9 px-3 border-neutral-300">← Sebelumnya</Button>
            <Button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} variant="outline" className="h-9 px-3 border-neutral-300">Berikutnya →</Button>
          </div>
        </div>
      )}
    </div>
  );
}
